/* eslint-disable react/no-unknown-property */
import "@/styles/escaperoom/inspect-item.scss";

import { Html, PresentationControls, useKeyboardControls } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import React, { useEffect, useRef, useState } from "react";
import { GrFormClose } from "react-icons/gr";

import { useStateContext } from "@/context/context-provider";

import { InfoBook } from "./info-book";

const maxZoom = 1.5;

const InspectItem = () => {
    const [open, setOpen] = useState(false);
    const { inspectedItem, focusedItem, setInspectedItem, setInPointerLock, displayTextCode, setDisplayTextCode, endScreenOpen, items } =
        useStateContext();

    const inspect = useKeyboardControls((state) => state.inspect);
    const escape = useKeyboardControls((state) => state.escape);
    const zoomIn = useKeyboardControls((state) => state.zoomIn);

    const activeItem = (inspectedItem?.uuid && items?.[inspectedItem.uuid]) || inspectedItem;

    // State & Ref
    const [startAnimation, setStartAnimation] = useState(false);
    const [endAnimation, setEndAnimation] = useState(false);
    const [zoom, setZoom] = useState(1);
    const itemRef = useRef();

    const overlayConfig = inspectedItem?.overlay || {};
    const isItemType = inspectedItem?.type === "item";

    /***************** Close and Open *******************/

    const closeInspectedItem = () => {
        setEndAnimation(true);
        setInPointerLock(true);
        setOpen(false);
        setTimeout(
            () => {
                setInspectedItem(null);
                setDisplayTextCode("");
            },
            isItemType ? 300 : 10
        );
    };

    const openInspectedItem = () => {
        setInspectedItem(focusedItem);
        setInPointerLock(false);
        setOpen(true);
        setStartAnimation(true);
    };

    useEffect(() => {
        if (!inspectedItem || !overlayConfig.enableInput) {
            return;
        }

        const maxLength = overlayConfig.maxLength || 10;

        const handleKeyDown = (e) => {
            if (e.key === "Enter") {
                if (overlayConfig.clearOnEnter !== false) {
                    setDisplayTextCode("");
                }
            } else if (e.key === "Backspace") {
                setDisplayTextCode((prev) => prev.slice(0, -1));
            } else if (/[a-zA-Z0-9]/.test(e.key) && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
                setDisplayTextCode((prev) => (prev.length < maxLength ? prev + e.key : prev));
            }
        };

        globalThis.addEventListener("keydown", handleKeyDown);
        return () => globalThis.removeEventListener("keydown", handleKeyDown);
    }, [inspectedItem, overlayConfig, setDisplayTextCode]);

    // Escape Key Handling
    useEffect(() => {
        if (inspectedItem && !startAnimation && !endAnimation) {
            closeInspectedItem();
        }
    }, [escape]);

    // Inspect Key Handling
    useEffect(() => {
        if (inspect && !startAnimation && !endAnimation && !endScreenOpen) {
            if (inspectedItem) {
                if (isItemType) {
                    closeInspectedItem();
                }
            } else if (focusedItem) {
                openInspectedItem();
            } else {
                setOpen(false);
            }
        }
    }, [inspect, focusedItem]);

    useEffect(() => {
        if (open) {
            setInspectedItem(focusedItem);
        }
    }, [focusedItem]);

    // Scale & Zoom Animations
    useFrame((state, delta) => {
        if (startAnimation && isItemType) {
            if (itemRef.current?.scale.x >= inspectedItem.inspectScale) {
                itemRef.current.scale.setScalar(inspectedItem.inspectScale);
                setStartAnimation(false);
            } else if (itemRef.current) {
                itemRef.current.scale.addScalar(2 * inspectedItem.inspectScale * delta);
            }
        }

        if (endAnimation && isItemType) {
            if (itemRef.current?.scale.x <= 0) {
                setEndAnimation(false);
            } else if (itemRef.current) {
                itemRef.current.scale.subScalar(2 * inspectedItem.inspectScale * delta);
            }
        } else if (endAnimation && !isItemType) {
            setEndAnimation(false);
        }

        const targetZoom = zoomIn ? Math.min(zoom + 0.1, maxZoom) : Math.max(zoom - 0.1, 1);
        setZoom(targetZoom);
        state.camera.zoom = targetZoom;
        state.camera.updateProjectionMatrix();
    });

    return (
        <>
            <InfoBook />

            {inspectedItem && (
                <>
                    <PresentationControls enabled={isItemType} polar={[-Infinity, Infinity]} cursor={false} global>
                        <group
                            ref={itemRef}
                            position={isItemType ? inspectedItem.inspectPosition || [0, 0, 0] : inspectedItem.position || [0, 0, 0]}
                            scale={isItemType && startAnimation ? 0 : inspectedItem.inspectScale || 1}
                            rotation={isItemType ? inspectedItem.inspectRotation || [0, 0.5, 0] : inspectedItem.rotation || [0, 0, 0]}
                            onPointerEnter={(e) => {
                                e.stopPropagation();
                                document.body.style.cursor = "pointer";
                            }}
                            onPointerLeave={() => {
                                document.body.style.cursor = "cursor";
                            }}
                            onClick={inspectedItem.clickEvent || undefined}
                            onPointerMissed={() => {
                                if (!isItemType) {
                                    closeInspectedItem();
                                }
                            }}>
                            {/* Meshes */}
                            {inspectedItem.mesh?.map((mesh) => (
                                <mesh
                                    key={mesh.uuid || mesh.name}
                                    geometry={mesh.geometry}
                                    material={mesh.material}
                                    position={mesh.position}
                                    rotation={mesh.rotation}
                                    scale={mesh.scale}
                                    ref={mesh.ref}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        mesh.clickEvent?.();
                                    }}
                                    onContextMenu={(e) => {
                                        e.stopPropagation();
                                        mesh.contextMenuEvent?.();
                                    }}
                                    onPointerEnter={(e) => {
                                        if (mesh.hoverEvent) {
                                            e.stopPropagation();
                                            document.body.style.cursor = "pointer";
                                        }
                                    }}
                                    onPointerLeave={() => {
                                        if (mesh.hoverEvent) {
                                            document.body.style.cursor = "default";
                                        }
                                    }}
                                />
                            ))}

                            {/* Generisches Overlay für Text / Code Anzeige */}
                            {overlayConfig.showText && (
                                <Html
                                    className={`inspect-overlay-container ${overlayConfig.className || ""} ${
                                        overlayConfig.hasBackground ? "with-background" : ""
                                    }`}
                                    center>
                                    <div className="inspect-display-content">
                                        {displayTextCode || <span className="placeholder">{overlayConfig.placeholder}</span>}
                                    </div>
                                </Html>
                            )}
                        </group>
                    </PresentationControls>

                    {/* 2D HTML UI Elements (Buttons / Close) */}
                    <Html>
                        {inspectedItem.showCloseBtn !== false && (
                            <div className="close-btn" onClick={closeInspectedItem}>
                                <GrFormClose size="3rem" />
                            </div>
                        )}

                        {activeItem?.inspectButtons && (
                            <div className="action-button-container">
                                {activeItem.inspectButtons.map((button) => (
                                    <button
                                        type="button"
                                        key={"button_" + button.name}
                                        className="actionButton"
                                        disabled={button.disabled}
                                        onClick={() => !button.disabled && button.clickEvent?.()}>
                                        {button.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </Html>
                </>
            )}
        </>
    );
};

export default InspectItem;
